// Rutgers University-Camden: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Rutgers' Office of Institutional Research and Academic Planning posts
//    Camden Common Data Sets through "Camden CDS_2023-2024_final_V1.pdf";
//    no 2024-2025 or 2025-2026 edition is posted. So the first-year counts
//    come from the U.S. Department of Education's IPEDS Admissions survey for
//    Fall 2024, first-time degree-seeking students (College Navigator and the
//    ADM2024 complete data file, unit 186371), a year newer than the CDS.
//  - Rutgers Undergraduate Admissions: Apply (first-year early action and
//    regular decision deadlines, no year on the page), How We Make Decisions
//    (Rutgers-Camden tab: decision guarantees, high school course
//    requirements) and First-Year Applicants (testing policy by campus).
//    Quirk: the Rutgers-Camden Application Dates and Deadlines page still
//    shows only the fall 2026 table (applications November 1 and December 1,
//    2025; decisions posted by January 31 and February 28, 2026), so it is
//    not used for fall 2027. The decision dates here are the Camden
//    guarantees on How We Make Decisions (by February 15 and March 31), the
//    page that already shows the fall 2026 admitted-student profile.
//  - Rutgers Scarlet Hub, Rutgers Students Cost of Attendance, 2026-2027:
//    the In-State and Out-of-State Camden Full-Time Undergraduate COA PDFs,
//    Camden College of Arts and Sciences, on campus (each column sums to its
//    own total). The Business and Nursing tuition and fees differ and are
//    named in the note. Admissions' Tuition and Fees page lists food and
//    housing at $21,943, $3 below the COA's $21,946; the COA is used.
//  - College Scorecard, school id 186371 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=186371#admsns',
        read: '2026-10-01',
    },
    oirap: {
        label: 'Rutgers Institutional Research and Academic Planning, Common Data Set',
        url: 'https://oirap.rutgers.edu/ReportingCommonDataSet.html',
        read: '2026-10-01',
    },
    apply: {
        label: 'Rutgers Undergraduate Admissions, Apply',
        url: 'https://admissions.rutgers.edu/apply',
        read: '2026-10-01',
    },
    decisions: {
        label: 'Rutgers Undergraduate Admissions, How We Make Decisions',
        url: 'https://admissions.rutgers.edu/apply/how-we-make-decisions',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Rutgers Undergraduate Admissions, First-Year Applicants',
        url: 'https://admissions.rutgers.edu/apply/first-year-applicants',
        read: '2026-10-01',
    },
    costs: {
        label: 'Rutgers Scarlet Hub, Rutgers Students Cost of Attendance',
        url: 'https://scarlethub.rutgers.edu/financial-services/cost-of-attendance/rutgers-students-cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?186371-Rutgers-University-Camden',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'rutgers-university-camden',
    kbId: 'rutgers_university_camden',
    name: 'Rutgers University-Camden',
    shortName: 'Rutgers-Camden',
    aliases: [],
    location: 'Camden, NJ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Rutgers identity guidelines); decoration only.
    branding: {
        primary: '#CC0033',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/rutgers_university_camden_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 32840,
        admitted: 21768,
        enrolled: 531,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Rutgers-Camden publishes no first-year acceptance rate by school or major, and its newest posted Common Data Set is the 2023-2024 edition, so the counts above come from the federal IPEDS survey.',
        source: 'oirap',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to a Rutgers-Camden school: the College of Arts and Sciences, the School of Business or the School of Nursing. Two programs ask for more:',
        rows: [
            { school: 'School of Nursing-Camden', requirement: 'Your 2 years of high school science must include one year each of biology and chemistry. Every Camden school expects 16 academic units, including 4 years of English, 2 of one foreign language and 3 of math through Algebra II.' },
            { school: 'BS/DO program', requirement: 'You must send at least one SAT or ACT score to complete the application.', source: 'firstYear' },
        ],
        source: 'decisions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular decision', value: 'December 1' },
        rows: [
            { plan: 'Early action', deadline: 'November 1', decision: 'By February 15', binding: false },
            { plan: 'Regular decision', deadline: 'December 1', decision: 'By March 31', binding: false },
        ],
        note: 'The decision dates are the Rutgers-Camden guarantees on Rutgers\' How We Make Decisions page; applications are read as they become complete with every required credential.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Rutgers-Camden is test-optional: the SAT or ACT is optional, and applying without scores still gets full consideration. Scores you send are reviewed, so do not send any you do not want considered. The BS/DO program requires a score. Rutgers says it keeps its test-optional and test-blind policies through 2027.',
        source: 'firstYear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [15381, 36831] },
            { item: 'Required fees', amounts: [3829, 3829] },
            { item: 'Food and housing (on campus)', amounts: [21946, 21946] },
            { item: 'Books', amounts: [1504, 1504] },
            { item: 'Transportation', amounts: [1026, 1598] },
            { item: 'Loan fees', amounts: [110, 110] },
            { item: 'Miscellaneous', amounts: [3704, 3704] },
        ],
        totals: [47500, 69522],
        note: 'For a full-time Camden College of Arts and Sciences undergraduate living on campus. In-state tuition and fees are $15,844 and $3,909 in the School of Business and $18,002 and $4,195 in the School of Nursing. Rutgers calls these estimates.',
        source: 'costs',
        netPrice: {
            average: 15732,
            byIncome: [
                { income: '$0 to $30,000', amount: 7480 },
                { income: '$30,001 to $48,000', amount: 11862 },
                { income: '$48,001 to $75,000', amount: 13932 },
                { income: '$75,001 to $110,000', amount: 19419 },
                { income: 'Over $110,000', amount: 24651 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Rutgers-Camden\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
